/**
 * The hover/focus readout for every chart: value first (strong), series second, a line key in the series colour.
 * Labels are rendered as React text (never HTML). Owner: slice web-charts (#1115).
 */
import type * as React from 'react'
import { formatValue, type Unit } from './format'

export interface TooltipRow {
  key: string
  label: string
  color?: string
  value: number | null
  n?: number | null
}

export function tooltipText(title: string, rows: TooltipRow[], unit: Unit): string {
  return [
    title,
    ...rows.map((r) => `${r.label}: ${r.value === null ? 'no data' : formatValue(unit, r.value)}${r.n ? ` (n=${r.n})` : ''}`),
  ].join(', ')
}

export function ChartTooltip({
  x,
  y,
  containerWidth,
  title,
  rows,
  unit,
}: {
  x: number
  y: number
  containerWidth: number
  title: string
  rows: TooltipRow[]
  unit: Unit
}) {
  const w = 200
  const left = Math.max(0, Math.min(containerWidth - w, x + 12 > containerWidth - w ? x - w - 12 : x + 12))
  const style: React.CSSProperties = { left, top: Math.max(0, y - 8), width: w }
  return (
    <div
      role="tooltip"
      data-chart-tooltip
      className="pointer-events-none absolute z-10 rounded-md border border-border bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md"
      style={style}
    >
      <div className="mb-1 text-muted-foreground">{title}</div>
      <ul className="space-y-0.5">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2">
            {r.color ? <span aria-hidden className="inline-block h-0.5 w-3 shrink-0 rounded-full" style={{ background: r.color }} /> : null}
            <span className="tabular font-semibold text-foreground">{r.value === null ? 'no data' : formatValue(unit, r.value)}</span>
            <span className="min-w-0 truncate text-muted-foreground">{r.label}</span>
            {r.n ? <span className="tabular ml-auto text-muted-foreground">n={r.n}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
